/* eslint-disable react-hooks/exhaustive-deps */
import useSnackbar from '@/hooks/useSnackbar';
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { StatusCode } from '../../../../../api/enumerations';
import { listAnnouncements } from '../../../../../api/theHive/announcement';
import { AnnouncementData } from '../../../../../api/theHive/announcement/types';
import { listNoticeCompanies } from '../../../../../api/theHive/notices';
import {
  ListNoticeCompaniesParams,
  NoticeCompaniesData,
} from '../../../../../api/theHive/notices/types';
import { IOSSwitch } from '../../../../../components/UI/Button';
import { StyledTextField } from '../../../../../components/UI/Typography';
import { Constants } from '../../../../../constants/hiveNotices';
import {
  IconSearchMS,
  IconArrowCircleLeftMS,
  IconContractEditMS,
  IconVisibilityMS,
  IconAddMS,
} from '../../../../../constants/icons';
import useErrorMessage from '../../../../../hooks/useErrorMessage';
import { BackButton } from '../../CompanyInfo/styles';
import { DocsSection } from '../components/AcceptanceDocsSection';
import { AnnouncementTable } from '../components/AnnouncementTable';
import { NoticeAttachments } from '../components/Attachments';
import { CompaniesTable } from '../components/CompaniesTable';
import { CoverageAreaSection } from '../components/CoverageAreaSection';
import { DocumentCard } from '../components/DocumentCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { NewAnnouncementModal } from '../components/NewAnnouncementModal';
import { TypologyGroupSection } from '../components/TypologyGroupSection';
import { useAcceptanceDocs } from '../hooks/acceptanceDocs';
import { useFetchCoverageOperations } from '../hooks/coverage';
import { useFetchNoticeDetails, useNoticeActions } from '../hooks/notices';
import { useTypologyGroups } from '../hooks/typology';
import { NoticeVisualizationLabel, StyledInputBase } from '../styles';
import { CustomDatePicker } from './CustomDatePicker';
import { DocumentDrawer } from './DocumentDrawer';
import { RemoveDocumentDialog } from './RemoveDocumentDialog';
import {
  NewTitleNotice,
  TabContainerNotice,
  StyledTextFieldDefault,
  GridButton,
  CreateNoticeButton,
  CancelButton,
  ContainerGrid,
  StyledDivider,
  AddDoc,
  AccreditationDocsSection,
} from './styles';
import { DocumentEntry } from './types';

export function NewNotice(): JSX.Element {
  const [showNewAnnouncementmodal, setShowNewAnnouncementmodal] =
    useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [extraDocuments, setExtraDocuments] = useState<DocumentEntry[]>([]);
  const [editingDocIndex, setEditingDocIndex] = useState<number | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeIsExtra, setRemoveIsExtra] = useState(false);
  const [removeDocName, setRemoveDocName] = useState('');
  const [contractServiceFile, setContractServiceFile] = useState<
    File | undefined
  >();
  const [searchTerm, setSearchTerm] = useState('');
  const [pageCompanies, setPageCompanies] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [totalPagesCompanies, setTotalPagesCompanies] = useState(12);
  const [companies, setCompanies] = useState<NoticeCompaniesData[]>([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [announcements, setAnnouncements] = useState<AnnouncementData[]>([]);
  const [pageAnnouncement, setPageAnnouncement] = useState(1);
  const [announcementsTotalPages, setAnnouncementsTotalPages] = useState(0);

  const searchTimeout = useRef<NodeJS.Timeout>();
  const ANNOUNCEMENTS_PER_PAGE = 10;
  const ITEMS_PER_PAGE = 10;

  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { notice } = useParams();
  const isNewNotice = location.pathname.includes('new');
  const noticeId = parseInt(notice as string, 10);

  const {
    name,
    setName,
    observation,
    setObservation,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    openToAccreditation,
    setOpenToAccreditation,
    isVisibleToProto,
    setIsVisibleToProto,
    contractFile,
    isLoading,
  } = useFetchNoticeDetails({ noticeId, isNewNotice });
  const { handleFormSubmit } = useNoticeActions({
    noticeId,
    isNewNotice,
  });
  const {
    criteriasGroup,
    fetchTypologyGroups,
    isLoading: loadingCriteriasGroup,
  } = useTypologyGroups(noticeId);
  const { coverageData, getCoverage } = useFetchCoverageOperations({
    noticeId,
  });
  const {
    acceptanceDocs,
    fetchAcceptanceDocuments,
    isLoading: loadingAcceptanceDocs,
  } = useAcceptanceDocs(noticeId);

  const allAccreditationDocs = useMemo(
    () => [
      ...(contractFile && contractFile.download_url !== ''
        ? [contractFile]
        : []),
      ...acceptanceDocs,
    ],
    [contractFile, acceptanceDocs]
  );

  const existingDocNames = useMemo(
    () => [
      Constants.contractService,
      ...extraDocuments
        .filter((_, i) => i !== editingDocIndex)
        .map((d) => d.name),
    ],
    [extraDocuments, editingDocIndex]
  );

  const fetchCompanies = async (search: string): Promise<void> => {
    try {
      setIsSearching(true);

      const params: ListNoticeCompaniesParams = {
        publicNoticeId: noticeId,
        page: pageCompanies,
        size: ITEMS_PER_PAGE,
        search,
      };

      const response = await listNoticeCompanies(params);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      setCompanies(response.data || []);
      setTotalPagesCompanies(response.detail.total_pages || 0);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    if (!noticeId) return;
    fetchCompanies(searchTerm);
  }, [pageCompanies, searchTerm]);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setPageCompanies(1);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      fetchCompanies(newSearchTerm);
    }, 600);
  };

  useEffect(() => {
    if (isNewNotice) return;
    getCoverage();
    fetchTypologyGroups();
    fetchAcceptanceDocuments();
  }, [isNewNotice, noticeId]);

  const validateDates = useMemo(() => {
    return (start: Dayjs | null, end: Dayjs | null) => {
      if (start && end && start.isAfter(end)) {
        handleSnackbar(
          'A data de início não pode ser maior que a data final',
          true
        );
        return false;
      }
      return true;
    };
  }, []);

  const validateForm = useCallback(() => {
    const isValid =
      !!name &&
      !!observation &&
      !!startDate &&
      !!endDate &&
      validateDates(startDate, endDate) &&
      (!isNewNotice || !!contractServiceFile);

    setIsFormValid(isValid);
  }, [
    name,
    observation,
    startDate,
    endDate,
    validateDates,
    isNewNotice,
    contractServiceFile,
  ]);

  const navigateBack = useCallback((): void => {
    let basePath: string;
    if (isNewNotice) {
      basePath = '/the-hive/notices/';
    } else {
      const previousPath = location.pathname.split('/edit')[0];
      basePath = previousPath;
    }
    navigate(basePath);
  }, [isNewNotice, location.pathname, navigate]);

  const navigateAddCompany = (): void => {
    if (isNewNotice) {
      navigate(`${location.pathname}/add-company`);
    } else {
      const basePath = location.pathname.split('/edit')[0];
      navigate(`${basePath}/add-company`);
    }
  };

  const handleSubmit = useCallback(async (): Promise<void> => {
    await handleFormSubmit({
      name,
      observation,
      startDate,
      endDate,
      openToAccreditation,
      isVisibleToProto,
      contractFile: contractServiceFile,
      extraDocuments,
    });
  }, [
    handleFormSubmit,
    name,
    observation,
    startDate,
    endDate,
    openToAccreditation,
    isVisibleToProto,
    contractServiceFile,
    extraDocuments,
  ]);

  const fetchAnnouncements = async (): Promise<void> => {
    try {
      const response = await listAnnouncements(
        noticeId,
        pageAnnouncement,
        ANNOUNCEMENTS_PER_PAGE
      );

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente');

      setAnnouncements(response.data);
      setAnnouncementsTotalPages(response.detail.total_pages || 0);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleAnnouncementUpdated = useCallback(async () => {
    await fetchAnnouncements();
  }, [fetchAnnouncements, pageAnnouncement]);

  useEffect(() => {
    if (noticeId) {
      fetchAnnouncements();
    }
  }, [noticeId, pageAnnouncement]);

  const handleStartDateChange = useCallback(
    (newValue: Dayjs | null): void => {
      setStartDate(newValue);
      validateForm();
    },
    [validateForm]
  );

  const handleEndDateChange = useCallback(
    (newValue: Dayjs | null): void => {
      setEndDate(newValue);
      validateForm();
    },
    [validateForm]
  );

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setName(event.target.value);
  };

  const handleObservationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setObservation(event.target.value);
  };

  const handleOpenDrawer = useCallback((): void => {
    setEditingDocIndex(null);
    setDrawerOpen(true);
  }, []);

  const handleOpenEditDrawer = useCallback((index: number): void => {
    setEditingDocIndex(index);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback((): void => {
    setDrawerOpen(false);
    setEditingDocIndex(null);
  }, []);

  const handleDeleteDocument = useCallback((): void => {
    if (editingDocIndex === null) return;
    setExtraDocuments((prev) => prev.filter((_, i) => i !== editingDocIndex));
    setDrawerOpen(false);
    setEditingDocIndex(null);
  }, [editingDocIndex]);

  const handleOpenRemoveContract = (): void => {
    setRemoveIsExtra(false);
    setRemoveDocName('');
    setRemoveDialogOpen(true);
  };

  const handleOpenRemoveExtra = (): void => {
    setRemoveIsExtra(true);
    setRemoveDocName(
      editingDocIndex !== null
        ? extraDocuments[editingDocIndex]?.name ?? ''
        : ''
    );
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = (): void => {
    if (removeIsExtra) {
      handleDeleteDocument();
    } else {
      setContractServiceFile(undefined);
    }
    setRemoveDialogOpen(false);
  };

  const handleAddDocument = useCallback(
    (doc: DocumentEntry): void => {
      if (editingDocIndex !== null) {
        setExtraDocuments((prev) => {
          const updated = [...prev];
          updated[editingDocIndex] = doc;
          return updated;
        });
        setEditingDocIndex(null);
      } else {
        setExtraDocuments((prev) => [...prev, doc]);
      }
    },
    [editingDocIndex]
  );

  const handleShowNewAnnouncementmodal = useCallback(() => {
    setShowNewAnnouncementmodal(true);
  }, []);

  const handleCloseNewAnnouncementmodal = useCallback(() => {
    setShowNewAnnouncementmodal(false);
  }, []);

  useEffect(() => {
    validateForm();
  }, [
    name,
    observation,
    startDate,
    endDate,
    contractServiceFile,
    validateForm,
  ]);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <BackButton onClick={navigateBack}>{IconArrowCircleLeftMS}</BackButton>
        <TabContainerNotice>
          {!isNewNotice && (
            <NoticeVisualizationLabel container gap={2}>
              <Grid item>{IconVisibilityMS}</Grid>
              <Grid item>
                <Typography sx={{ fontWeight: 600 }}>
                  {Constants.visibleToProto}
                </Typography>
                <Typography>{Constants.activateVisibility}</Typography>
              </Grid>
              <Grid
                item
                flex={1}
                display="flex"
                justifyContent="end"
                alignSelf="center"
              >
                <IOSSwitch
                  checked={isVisibleToProto}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setIsVisibleToProto(e.target.checked);
                  }}
                />
              </Grid>
            </NoticeVisualizationLabel>
          )}
          <NewTitleNotice>{isNewNotice ? 'Novo edital' : name}</NewTitleNotice>
          <Grid item container mt={1} spacing={2}>
            <Grid item container xs={isNewNotice ? 3 : 6}>
              <Grid item>
                <StyledTextFieldDefault
                  label="Nome"
                  variant="outlined"
                  value={name}
                  onChange={handleNameChange}
                  inputProps={{ maxLength: 80 }}
                  required
                />
              </Grid>
              {!isNewNotice && (
                <GridButton item ml={1}>
                  <NoticeAttachments
                    noticeId={noticeId}
                    disabled={isNewNotice}
                  />
                </GridButton>
              )}
            </Grid>
            <Grid item container xs={6} spacing={1}>
              <Grid item xs={6} display="flex" justifyContent="right">
                <CustomDatePicker
                  label="Início da Vigência*"
                  value={startDate}
                  onChange={handleStartDateChange}
                  placeholder="Data de início"
                />
              </Grid>
              <Grid item xs={6}>
                <CustomDatePicker
                  label="Fim da Vigência*"
                  value={endDate}
                  onChange={handleEndDateChange}
                  placeholder="Data do fim"
                />
              </Grid>
            </Grid>
            {isNewNotice && (
              <Grid item xs={3}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography>{Constants.openToAccreditation}</Typography>
                  <IOSSwitch
                    checked={openToAccreditation}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setOpenToAccreditation(e.target.checked);
                    }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
          <Grid item container xs={12} mt={4}>
            <StyledTextField
              label="Observação"
              variant="outlined"
              multiline
              rows={3}
              placeholder="Escreva uma observação"
              value={observation}
              onChange={handleObservationChange}
              inputProps={{ maxLength: 300 }}
              required
            />
          </Grid>
          {isNewNotice && (
            <>
              <StyledDivider />
              <AccreditationDocsSection>
                {IconContractEditMS}
                {Constants.accreditationDocuments}
              </AccreditationDocsSection>
              <Typography mt={1}>{Constants.defineDocuments}</Typography>
              <Box mt={2} mb="24px">
                <AddDoc
                  onClick={handleOpenDrawer}
                  disabled={extraDocuments.length >= 15}
                >
                  {IconAddMS}
                  {Constants.addDocumentToAccept}
                </AddDoc>
              </Box>
              <Box
                display="flex"
                flexWrap="wrap"
                columnGap="43px"
                rowGap="24px"
              >
                <Box maxWidth={330}>
                  <DocumentCard
                    name={Constants.contractService}
                    file={contractServiceFile}
                    onFileSelect={setContractServiceFile}
                    onDelete={handleOpenRemoveContract}
                    inputId="contractServiceUpload"
                  />
                  <Typography mt={1} fontSize="12px" color="#595959">
                    {Constants.signDocObs}
                  </Typography>
                </Box>
                {extraDocuments.map((doc, index) => (
                  <Box maxWidth={330} key={doc.name}>
                    <DocumentCard
                      name={doc.name}
                      file={doc.file}
                      onEdit={() => handleOpenEditDrawer(index)}
                    />
                  </Box>
                ))}
              </Box>
              <DocumentDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                onAddDocument={handleAddDocument}
                editDoc={
                  editingDocIndex !== null
                    ? extraDocuments[editingDocIndex]
                    : undefined
                }
                onDeleteDoc={
                  editingDocIndex !== null ? handleOpenRemoveExtra : undefined
                }
                existingNames={existingDocNames}
              />
            </>
          )}
          {!isNewNotice && (
            <Stack direction="row" alignItems="center" gap={1} mt={2}>
              <Typography>{Constants.openToAccreditation}</Typography>
              <IOSSwitch
                checked={openToAccreditation}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setOpenToAccreditation(e.target.checked);
                }}
              />
            </Stack>
          )}
          <Grid item container justifyContent="end" xs={12} gap={4} mt={4}>
            <CancelButton onClick={navigateBack}>
              {Constants.cancel}
            </CancelButton>
            <CreateNoticeButton
              onClick={handleSubmit}
              disabled={!isFormValid}
              style={{
                opacity: isFormValid ? 1 : 0.5,
                cursor: isFormValid ? 'pointer' : 'not-allowed',
              }}
            >
              {isNewNotice ? 'Criar edital' : 'Confirmar alterações'}
            </CreateNoticeButton>
          </Grid>
          {!isNewNotice && (
            <>
              <CoverageAreaSection
                data={coverageData}
                fetchCoverage={getCoverage}
              />
              <TypologyGroupSection
                criteriasGroup={criteriasGroup}
                fetchTypologyGroups={fetchTypologyGroups}
                isLoading={loadingCriteriasGroup}
              />
              <DocsSection
                data={allAccreditationDocs}
                isLoading={loadingAcceptanceDocs}
              />
              <ContainerGrid container>
                <Grid item display="flex" justifyContent="end" xs={12}>
                  <Button
                    sx={{ mb: 3 }}
                    onClick={handleShowNewAnnouncementmodal}
                  >
                    novo comunicado
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <AnnouncementTable
                    data={announcements}
                    page={pageAnnouncement}
                    totalPages={announcementsTotalPages}
                    setPage={setPageAnnouncement}
                    onAnnouncementUpdated={handleAnnouncementUpdated}
                  />
                </Grid>
              </ContainerGrid>
              <ContainerGrid container>
                <Grid
                  item
                  display="flex"
                  alignItems="center"
                  justifyContent="end"
                  xs={12}
                >
                  <Box display="flex" alignItems="center" mr={4}>
                    <StyledInputBase
                      placeholder="buscar empresa"
                      inputProps={{ 'aria-label': 'pesquisar', maxLength: 14 }}
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      startAdornment={
                        <InputAdornment
                          position="start"
                          sx={(theme) => ({
                            color: theme.palette.secondary.main,
                          })}
                        >
                          {IconSearchMS}
                        </InputAdornment>
                      }
                    />
                  </Box>
                  <Button onClick={navigateAddCompany}>
                    adicionar empresa
                  </Button>
                </Grid>
                <Grid item container mt={3}>
                  <CompaniesTable
                    data={companies}
                    page={pageCompanies}
                    totalPages={totalPagesCompanies}
                    setPage={setPageCompanies}
                    isLoading={isSearching}
                  />
                </Grid>
              </ContainerGrid>
            </>
          )}
        </TabContainerNotice>
      </LocalizationProvider>
      <NewAnnouncementModal
        announcements={announcements}
        setAnnouncements={setAnnouncements}
        isModalOpen={showNewAnnouncementmodal}
        onCloseModal={handleCloseNewAnnouncementmodal}
        onAnnouncementUpdated={handleAnnouncementUpdated}
        noticeId={noticeId}
      />
      <RemoveDocumentDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={handleConfirmRemove}
        title={
          removeIsExtra
            ? Constants.removeExtraTitle
            : Constants.removeContractTitle
        }
        subtitle={
          removeIsExtra
            ? Constants.removeExtraSubtitle.replace(
                '{{docName}}',
                removeDocName
              )
            : Constants.removeContractSubtitle
        }
        confirmLabel={
          removeIsExtra ? Constants.removeDocumentLabel : Constants.removeLabel
        }
      />
    </>
  );
}
